import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import {useDispatch} from "react-redux";
import {useLocation, useOutletContext} from "react-router-dom";
import {
    currencyFormat,
    DATE_TIME_FORMAT_NAME_MODEL,
    EMPTY_VALUES,
    getDateFormat,
    getTimeFormat,
    tryGetAddress
} from "../../../../../Apis/functions";
import {
    CalendarIcon,
    IconClockTable,
    IconTittleCafeteria,
    MarkerTitle,
    MealTimeTitle,
    ServiceTypeChip
} from "../../../../../components/buttons/IconButtons";
import CustomGenericTittleIconDataChip from "../../../../../components/CustomGenericTittleIconDataChip";
import CustomUserChip from "../../../../../components/CustomUserChip";
import { CustomLevelCardStyle } from "../../../../../components/Styles";
import { STATUS_MODEL } from "../../../../../enums/statusEnum";
import SelectCoffeeShopApp from "../../Functionality/SelectCoffeShopApp";
import CustomChip from '../../../../../components/CustomChip';
import {colors} from '../../../../../enums/colors';
import {MEAL_CATEGORY} from '../../../Dining/Manage/Models/Model';
import {debounce} from 'lodash';
import CustomTooltip from '../../../../../components/CustomTooltip';

/**
 *
 * @Author      Jhovanny Fragoso Marquez
 * @JobTitle    Frontend Developer
 * @Email       jfragoso@ibl.mx
 *
 * Renders the details of an order, including cafeteria info, location, time window,
 * and any child components passed to it (e.g. dish selection summary).
 *
 * - Uses Geocode API to convert latitude and longitude into a readable address.
 * - Translates text according to current language (supports 12h or 24h format).
 * - Fetches `meal` data from React Router outlet context.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ReactNode} [props.children=null] - Optional child elements to render inside the order detail card.
 * @returns {JSX.Element} A styled card component displaying order metadata.
 */

const OrderDetailsCard = ({ children = null, costTotal }) => {
    const { t } = useTranslation();
    const { meal } = useOutletContext()
    const { mealtimes, foodieSetting } = SelectCoffeeShopApp()
    const dispatch = useDispatch();
    const [location, setLocation] = useState('');
    const styles = CustomLevelCardStyle(STATUS_MODEL.ACCEPTED, false);
    const [subtotal, setSubtotal] = useState(0);
    const [subsidy, setSubsidy] = useState(0);

    const locationPath = useLocation();
    let { state } = locationPath;
    let foodieRequestId = state?.foodieRequestId;
    const mealTimeName = mealtimes?.find(mealTime => mealTime.id === meal.mealTime)?.name

    const handleMapMove = debounce((lat, lng) => {
        tryGetAddress(lat, lng, dispatch, setLocation);
    }, 800);

    useEffect(() => {
        if (meal.latitude && meal.longitude) {
            setLocation(t('loading'));
            handleMapMove(meal.latitude, meal.longitude);
        }
    }, [meal.latitude, meal.longitude]);


    /**
     * Subtotal and subsidy calculation based on the configuration.
     */
    useEffect(() => {
        if(foodieSetting?.id){
            setSubtotal(foodieSetting.isBaseCost ? foodieSetting.baseCost : costTotal);
            const useExtraSubsidy = foodieSetting.isExtraMealSubsidy && foodieRequestId !== null && foodieRequestId !== undefined;
            setSubsidy(useExtraSubsidy ? foodieSetting.extraMealSubsidy : foodieSetting.subsidy);
        }
    }, [costTotal, foodieSetting]);

    return (
        <div style={styles.container}>
            <div style={styles.levelCard}>
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                    <div className="d-flex align-items-center gap-2">
                        <MealTimeTitle className="w-40px-h-40px" />
                        <p className="fs-5 mb-0 ibl-font-w-600">{t('review_and_confirm_order')}</p>
                        <CustomChip
                            background={colors.secondary100}
                            borderColor={colors.secondary150}
                            fontColor={colors.secondary700}
                            legend={foodieRequestId != null && foodieRequestId !== undefined ? t(MEAL_CATEGORY.EXTRA) : t(MEAL_CATEGORY.REGULAR) }
                        />
                    </div>
                    <div className="d-flex align-items-center gap-3 flex-column flex-md-row">
                        <div className='d-flex flex-row align-items-center gap-2'>
                            <span className="fs-12px ibl-font-w-400">{t('cafeteria_serving_date')}</span>
                            <div className="d-flex flex-column align-items-start">
                                <CustomUserChip
                                    className="mt-0 text-capitalize"
                                    Icon={CalendarIcon}
                                    name={getDateFormat(meal.servingDate, false, DATE_TIME_FORMAT_NAME_MODEL.MMMM_YYYY)}
                                    showAvatar={true}
                                    classNameIcon='icon-color-shades-400'
                                />
                            </div>
                        </div>
                        <div className='vertical-separator-shades-950' />

                        <div className='d-flex flex-row justify-content-center align-items-center gap-2'>
                            <span className="fs-12px ibl-font-w-400">{t('service_time')}</span>
                            <div className="d-flex flex-column align-items-start">
                                <CustomUserChip
                                    className="mt-0 text-capitalize"
                                    Icon={ServiceTypeChip}
                                    name={mealTimeName}
                                    showAvatar={true}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={"d-flex flex-column w-100 gap-3 p-16px"}>
                <div className="row mt-3">
                    <div className="col-12 col-md-3 mb-3 mb-md-0">
                        <div>
                            <CustomGenericTittleIconDataChip title={t("access_zone")} />
                            <div className="mb-5 mt-2">
                                <div className="d-flex gap-2 py-1 align-items-baseline">
                                    <div className='flex-shrink-0'>
                                        <MarkerTitle width={16} height={16}/>
                                    </div>
                                    <CustomTooltip
                                            text={location ? location : t('no_location')}
                                            showIfTruncated={true}
                                            classes="text-ellipsis-2-rows fs-14px"
                                    />
                                </div>
                                <div className="d-flex gap-2 py-1 align-items-center">
                                    <div className='flex-shrink-0'>
                                        <IconTittleCafeteria width={16} height={16} />
                                    </div>
                                    <p className={"fs-14px"}>{meal?.cafeteriaName || t("no_record")}</p>
                                </div>
                            </div>
                            <CustomGenericTittleIconDataChip title={t("pickup_window")} />
                            <div className="mb-5 mt-2">
                                { meal.openTime ?
                                    <div className="d-flex gap-2 py-1 align-items-center">
                                        <IconClockTable className="w-h-16 icon-color-general-950" />
                                        <p>
                                            <p>
                                                {getTimeFormat(`1970-01-01T${meal.openTime}`)} - {getTimeFormat(`1970-01-01T${meal.closeTime}`)}
                                            </p>

                                        </p>
                                    </div>
                                    : EMPTY_VALUES.DEFAULT
                                }
                            </div>

                            <CustomGenericTittleIconDataChip title={t("receipt")} />
                            <div className="d-flex justify-content-between mb-2">
                                <div className="font-color-general-950">{t('subtotal')}</div>
                                <div className="">{"$ " + currencyFormat(subtotal)}</div>
                            </div>
                            <div className="d-flex justify-content-between">
                                <div className="font-color-general-950">{t('subsidy')}</div>
                                <div className="">{"$ " + currencyFormat(subsidy)}</div>
                            </div>
                            <CustomGenericTittleIconDataChip title={t("")} />
                            <div className="d-flex justify-content-between mb-2">
                                <div className="ibl-font-w-600 font-color-general-950">{t('total')}</div>
                                <div className="ibl-font-w-600 font-color-general-950 ">{"$ " + currencyFormat(subsidy > subtotal ? 0 : subtotal - subsidy )}</div>
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-9">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OrderDetailsCard







